/**
 * Pattern-matched canned responses — fallback for when the live Gemini
 * endpoint is unavailable (no GEMINI_API_KEY on the server, rate-limited,
 * or the user has explicitly opted into canned mode for any reason).
 *
 * Default chat mode in production is "live" (see useAIChat). The OPENER
 * + DEFAULT_REPLY here only show if the live stream fails to produce
 * any tokens.
 *
 * Replies are written in João's voice — concise, technical, friendly.
 */

interface Pattern {
  /** Lowercased keyword test. Order matters — first match wins. */
  match: RegExp;
  reply: string;
}

const HIRE_LINK = "Tap the Hire button in the top-right to send a brief, or just email joao@jgson.net.";

const PATTERNS: Pattern[] = [
  {
    match: /\bhi\b|\bhello\b|\bhey\b|good (morning|afternoon|evening)/i,
    reply:
      "Hey — welcome to JGson. I'm a canned-response demo. Live streaming Claude / GPT / open-model wiring is something João builds for clients — not a feature of this brochure site. Ask me what JGson does, who João is, or how to hire.",
  },
  {
    match: /\b(what|who).*(jgson|company|studio|do you build|services?)\b/i,
    reply:
      "JGson is the one-engineer studio of João Vidotto. We ship across eight skill areas: games, Unity & Unreal engines, AR/VR, native Android, React Native, AI integrations, fintech automation, and a career-long portfolio of mobile work. The cube on the home page is the navigation — each cubie opens a real demo.",
  },
  {
    match: /\bwho\b.*(jo[aã]o|vidotto|engineer|founder|behind)/i,
    reply:
      "João Guilherme Bittar Vidotto — ten years in mobile engineering, currently at Tonic3 (the UX division of W3). Based in Londrina, Brazil. Bilingual PT/EN. Ships in Unity/C#, Unreal/C++, Go, native Android (Kotlin), React Native, and now a lot of LLM integration work — including this site you're on.",
  },
  {
    match: /\b(hire|brief|contact|email|reach|engage|consult|book)\b/i,
    reply: `Three ways to start: ${HIRE_LINK} The brief form routes to João via Resend with a typical 24h reply.`,
  },
  {
    match: /\b(cubek|rubik)\b/i,
    reply:
      "Cubek is João's Rubik's-cube simulator on the Play Store (com.JGSON.Cubek). It's where this site's cube concept comes from — the home page is literally a 2×2 Cubek. Features an AI Solver, 'Rhythm Hints' pulse-light teaching, AR Mode, and stats tracking.",
  },
  {
    match: /\b(magic|puzzle quest|mtg|d3 publisher)\b/i,
    reply:
      "Magic: Puzzle Quest — match-3 + RPG on the Magic: The Gathering IP. João was a Unity developer at Oktagon Games on it back in 2019; D3 Publisher shipped it. Still live on Google Play and the App Store. Tap the red Engines cubie for the rest of the Unity / Unreal credits.",
  },
  {
    match: /\b(doodle jump|lima sky)\b/i,
    reply:
      "Doodle Jump Adventure — entry in the Doodle Jump franchise (Lima Sky). João shipped it as a Unity developer at Oktagon Games in 2019. Retired from the official stores; QooApp still mirrors the build. Tap the red Engines cubie for the full credits.",
  },
  {
    match: /\boktagon\b/i,
    reply:
      "Oktagon Games is the Brazilian studio where João worked as a Unity developer in 2019. Two shipped credits from that period: Magic: Puzzle Quest (D3 Publisher) and Doodle Jump Adventure (Lima Sky). Both live as cards on the Engines cubie.",
  },
  {
    match: /\b(unity|unreal|c#|c\+\+|game engine|gamedev)\b/i,
    reply:
      "Unity (C#) and Unreal Engine (C++) end-to-end — gameplay programming, custom rendering, shaders, multiplayer netcode, editor tools, and full build/publish pipelines for PC, console, mobile, and XR. Shipped credits include Cubek (JGson, Play Store), Magic: Puzzle Quest (Oktagon Games / D3 Publisher, 2019), and Doodle Jump Adventure (Oktagon / Lima Sky, 2019). Tap the red Engines cubie for the deep dive.",
  },
  {
    match: /\b(react native|\brn\b|cross.?platform|expo|hermes)\b/i,
    reply:
      "Cross-platform with React Native — one TypeScript codebase, native performance on iOS and Android. Expo, native modules, deep links, push, EAS Build, and CI/CD for both stores. Tap the cyan React Native cubie for the live paired-mockup demo.",
  },
  {
    match: /\b(android|kotlin|jetpack|compose|native mobile)\b/i,
    reply:
      "Native Android — Kotlin + Jetpack Compose for the modern stuff, Java for the long-running ships. Public credits on Google Play: Cubek and Guia Guiné (both JGson). Additional native-Android work is under NDA. Tap the mint Mobile cubie for the stack details.",
  },
  {
    match: /\b(ar|vr|augment|reality|vuforia|xr|hololens)\b/i,
    reply:
      "AR & VR — Vuforia, AR Foundation, immersive UX. Past AR builds are under NDA or in pre-launch; reach out via Hire for case-study references. Tap the violet Augment cubie for the AR portal demo (stencil-buffer pass revealing a recreated scene).",
  },
  {
    match: /\b(trade|cypher|crypto|bot|fintech|trading|finance)\b/i,
    reply:
      "Trade is the fintech cubie. Two shipped credits: Cypher Bot — João's in-progress trading SaaS at app.cypher-bot.com — and an ATR-trailing-stop strategy published on Nelogica's Profit Pro marketplace (NTSL). The cubie shows a live-styled candlestick chart and outbound links to both.",
  },
  {
    match: /\b(nelogica|profit pro|ntsl|atr|trailing stop|profit plus)\b/i,
    reply:
      "ATR Trailing Stop com EMA — João's strategy published on the Nelogica Profit Pro marketplace (id 10748). EMA + ATR-based signal generation with adjustable sensitivity, Heikin Ashi smoothing, and real-time alerts. NTSL source. Tap the amber Trade cubie for the live demo + the YouTube walk-through.",
  },
  {
    match: /\b(ai|claude|llm|gpt|model|rag|agent|prompt)\b/i,
    reply:
      "AI work covers LLM apps (Anthropic Claude, OpenAI, Cloudflare Workers AI), agentic workflows with tool use, RAG pipelines, and production-grade prompt engineering. On the orchestration side, n8n is the usual glue — webhook → LLM → email/WhatsApp/Telegram/Slack pipelines. This chat is intentionally canned (zero LLM bill for a brochure site) — wiring real streaming Claude into your own product is exactly the kind of thing João ships for clients.",
  },
  {
    match: /\b(n8n|automation|workflow|webhook|drip|broadcast|crm|zapier|make\.com)\b/i,
    reply:
      "n8n is JGson's go-to for AI-flavored automation — webhook ingestion → LLM judgement call → outbound action (email, WhatsApp, Telegram, Slack). Common builds: automatic senders (drip campaigns, broadcast bots), CRM event responders (lead arrives → enrich → tag → reply via the right channel → log), and multi-channel chatbots with shared state. Self-hosted or n8n.cloud — same blueprint either way.",
  },
  {
    match: /\b(whatsapp|telegram|email|sender|outreach|messaging)\b/i,
    reply:
      "Multi-channel sending lives on the AI cubie. WhatsApp Business API, Telegram Bot, Resend / SendGrid for email, Slack — usually orchestrated via n8n so one workflow can fan out across channels with LLM-driven content per recipient. Drip campaigns, broadcasts, CRM responders all fit the same pattern.",
  },
  {
    match: /\b(stack|tech|tooling|framework|language)\b/i,
    reply:
      "This site: Vite + React 18 + TypeScript strict, Three.js via react-three-fiber, Tailwind v4, Zustand, Valibot. Backend is Node + Hono + SQLite (better-sqlite3) on a Contabo VPS, fronted by Cloudflare Tunnel so the origin never sees the public internet — no nginx, no certbot. Deployed via GitHub Actions on push to main. Built spec-first via github/spec-kit — see specs/001-jgson-core/ in the repo.",
  },
  {
    match: /\b(cube|navigation|rubik|how does this work)\b/i,
    reply:
      "The home page is a 2×2×2 Rubik's-style cube — eight cubies, each one a clickable nav target into a discipline. Drag to rotate, tap a cubie to enter. Visit all eight and the exploration meter at the top hits 100% — that unlocks a 'Trust Unlocked' card. Hint: the Konami code does something too, once you've cleared the meter.",
  },
  {
    match: /\b(spec|kit|spec-kit|methodology|workflow)\b/i,
    reply:
      "spec-kit (github/spec-kit) — GitHub's spec-driven development toolkit. Every feature on this site started as a spec.md (user stories, FRs, success criteria), then a plan.md (stack, scene graph, perf budget), then tasks.md (74+ shippable units, phased). The constitution.md sets the brand rules: mobile-first, accessibility AA, no dark patterns.",
  },
  {
    match: /\b(price|cost|rate|budget|how much)\b/i,
    reply: `Pricing scales with scope — a discrete game prototype, an AI feature for an existing app, and a long-form mobile build are all different. The brief form collects budget bands. Quickest path: ${HIRE_LINK}`,
  },
  {
    match: /\b(why|reason).*(this|site|cube)/i,
    reply:
      "Most engineering portfolios read like a list — and a list can't show that someone ships across games, mobile, AR, AI, and fintech without paragraphs that nobody finishes. The cube is the proof-of-craft: the site itself demonstrates the kind of interactive 3D / animated / responsive work JGson builds for clients.",
  },
];

const DEFAULT_REPLY =
  "Good question — I'm running in canned-response mode (live LLM hookup is a JGson-builds-it-for-you service, not a feature of this brochure site), so my reach is limited. Try asking what JGson builds, who João is, or how to hire. Or jump to a cubie from the home page.";

export function pickReply(userText: string): string {
  for (const p of PATTERNS) {
    if (p.match.test(userText)) return p.reply;
  }
  return DEFAULT_REPLY;
}

export const OPENER =
  "Hi — I'm JGson's site assistant, running Gemini Flash through João's Hono backend. Ask me what JGson builds, who João is, how to hire, or anything else.";

export const SUGGESTED_PROMPTS: readonly string[] = [
  "What does JGson build?",
  "Who is João?",
  "How do I hire you?",
  "Tell me about Cubek",
  "Can you build an AI feature?",
];
